import { Avatar, Box, Card, CardContent, CardMedia, Grid, IconButton, Modal, Tooltip, Typography } from '@mui/material';
import DOMPurify from 'dompurify';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CardHeader, CardLarge } from '../../../app/AppStyles';
import { GetDate, GetTime } from '../../../app/AppUtils';
import BackButton from '../../subcomponents/BackButton';
import ContentLoader from '../../subcomponents/ContentLoader';
import ListItemCard from '../../subcomponents/ListItemCard';
import PageLayout from '../../subcomponents/PageLayout';
import { fetchNewsArticle, resetNewsArticleManagementStatus } from './newsManagementSlice';

import EditIcon from '@mui/icons-material/Edit';
import { useState } from 'react';
import NewsEditCard from './NewsEditCard';

export default function NewsManagementPage() {
    const dispatch = useDispatch();
    const { id } = useParams();

    const { appLanguage, resourceFinal } = useSelector((state) => state.settings.resource.resources);
    const { newsArticle } = useSelector((state) => state.management.news);

    const [openEdit, setOpenEdit] = useState(false);

    useEffect(() => {
        dispatch(fetchNewsArticle(id));
    }, [id]);

    const callbackEditModal = () => {
        setOpenEdit(false);
        dispatch(resetNewsArticleManagementStatus());
    };

    return (
        <PageLayout
            title={`${newsArticle.data?.title ?? "-"}`}
            content={
                <ContentLoader
                    successCondition={newsArticle.status === "success"}
                    errorCondition={newsArticle.status === "failed"}
                    content={
                        <>
                            <Grid container spacing={1} sx={{ mt: { xs: 0, md: 1 } }}>
                                <Grid size={{ xs: 12, md: 12 }}>
                                    <ListItemCard backgroundStyle={{ height: 1 }}
                                        primaryText={newsArticle.data?.title}
                                        secondaryText={resourceFinal[appLanguage].words.TITLE}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <ListItemCard backgroundStyle={{ height: 1 }}
                                        primaryText={newsArticle.data?.placedBy}
                                        secondaryText={resourceFinal[appLanguage].words.PLACED_BY}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <ListItemCard backgroundStyle={{ height: 1 }}
                                        primaryText={`${GetDate(newsArticle.data?.datePlaced)} (${GetTime(newsArticle.data?.datePlaced)})`}
                                        secondaryText={resourceFinal[appLanguage].words.PLACED_DATE}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <Card sx={{ ...CardLarge }}>
                                        {newsArticle.data?.imageUrl && <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "center",
                                                alignItems: "center",
                                                backgroundColor: "#f5f5f5",
                                                borderRadius: 2,
                                                width: "100%",
                                                height: 250,
                                                overflow: "hidden",
                                            }}
                                        >
                                            <CardMedia
                                                component="img"
                                                image={newsArticle.data?.imageUrl}
                                                alt="article image"
                                                sx={{
                                                    objectFit: "none",
                                                    width: "auto",
                                                    height: "auto",
                                                    maxWidth: "100%",
                                                    maxHeight: "100%",
                                                }}
                                            />
                                        </Box>}
                                        <CardContent>
                                            <Typography variant="h6" sx={{ ...CardHeader }}>
                                                {newsArticle.data?.title}
                                            </Typography>
                                            <Typography variant="subtitle2" gutterBottom>
                                                {GetDate(newsArticle.data?.datePlaced)} ({GetTime(newsArticle.data?.datePlaced)}) |{" "}
                                                {newsArticle.data?.placedBy}
                                            </Typography>
                                            <Typography
                                                variant="body1"
                                                dangerouslySetInnerHTML={{
                                                    __html: DOMPurify.sanitize(newsArticle.data?.message),
                                                }}
                                            ></Typography>
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>

                            <Modal open={openEdit} onClose={callbackEditModal}>
                                <>
                                    <NewsEditCard handleClose={callbackEditModal} newsArticleId={id} />
                                </>
                            </Modal>
                        </>
                    }
                />
            } contentRight={
                <>
                    {newsArticle.status === "success" &&
                        <Tooltip title={resourceFinal[appLanguage].words.EDIT} placement="left" arrow>
                            <IconButton onClick={() => setOpenEdit(true)}>
                                <Avatar sx={{ bgcolor: 'secondary.main', p: 0.5 }}>
                                    <EditIcon sx={{ color: 'white.main', fontSize: 40 }} />
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    }
                    <BackButton desktop enableBorder />
                </>
            }
            bottomBar
            disableContentBackground
        />
    );
}
